import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { logout } from './authSlice';
import api from './config';

const initialState = {
  waiterCalls: [],
  roomServiceRequests: [],
  recordsTotal: 0,
  // Number of Pending requests, kept separate from the lists above so the
  // sidebar badge can refresh without clobbering the page's filtered view.
  pendingCount: 0,
  loading: false,
  updating: false,
  errors: []
};

export const fetchWaiterCalls = createAsyncThunk(
  'serviceRequests/fetchWaiterCalls',
  async ({ companyId, ...params }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/reservations/company/${companyId}/waiter-calls`, { params });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const fetchRoomServiceRequests = createAsyncThunk(
  'serviceRequests/fetchRoomServiceRequests',
  async ({ companyId, ...params }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.get(`/reservations/company/${companyId}/room-service-requests`, {
        params
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

// A cheap request (length: 1) since only `recordsTotal` is read - see the
// DatatableWrapper lesson on never sending length: 0 for a count-only call.
export const fetchPendingServiceCount = createAsyncThunk(
  'serviceRequests/fetchPendingCount',
  async ({ companyId, isHotel }, { rejectWithValue, dispatch }) => {
    try {
      const path = isHotel
        ? `/reservations/company/${companyId}/room-service-requests`
        : `/reservations/company/${companyId}/waiter-calls`;
      const response = await api.get(path, {
        params: { start: 0, length: 1, filters: { status: 'Pending' } }
      });
      return response.data?.recordsTotal || 0;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const updateWaiterCallStatus = createAsyncThunk(
  'serviceRequests/updateWaiterCallStatus',
  async ({ waiterCallId, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.patch(`/reservations/waiter-calls/${waiterCallId}/status`, {
        status
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

export const updateRoomServiceRequestStatus = createAsyncThunk(
  'serviceRequests/updateRoomServiceRequestStatus',
  async ({ requestId, status }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.patch(`/reservations/room-service-requests/${requestId}/status`, {
        status
      });
      return response.data;
    } catch (error) {
      if (error.response?.status === 401) dispatch(logout());
      return rejectWithValue(error.response?.data || error);
    }
  }
);

const serviceRequestSlice = createSlice({
  name: 'serviceRequests',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWaiterCalls.pending, (state) => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(fetchWaiterCalls.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.waiterCalls = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
      })
      .addCase(fetchWaiterCalls.rejected, (state, { payload }) => {
        state.loading = false;
        state.errors = payload;
      })
      .addCase(fetchRoomServiceRequests.pending, (state) => {
        state.loading = true;
        state.errors = [];
      })
      .addCase(fetchRoomServiceRequests.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.roomServiceRequests = payload?.data || [];
        state.recordsTotal = payload?.recordsTotal || 0;
      })
      .addCase(fetchRoomServiceRequests.rejected, (state, { payload }) => {
        state.loading = false;
        state.errors = payload;
      })
      .addCase(fetchPendingServiceCount.fulfilled, (state, { payload }) => {
        state.pendingCount = payload;
      })
      .addCase(updateWaiterCallStatus.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateWaiterCallStatus.fulfilled, (state, { payload }) => {
        state.updating = false;
        const updated = payload?.data;
        if (!updated) return;
        if (updated.status === 'Handled' && state.pendingCount > 0) state.pendingCount -= 1;
        const idx = state.waiterCalls.findIndex((c) => c._id === updated.waiterCallId);
        if (idx !== -1) state.waiterCalls[idx].status = updated.status;
      })
      .addCase(updateWaiterCallStatus.rejected, (state) => {
        // Update failures are surfaced via toast in the component; keep `errors`
        // reserved for fetch failures so the list doesn't swap to the error state.
        state.updating = false;
      })
      .addCase(updateRoomServiceRequestStatus.pending, (state) => {
        state.updating = true;
      })
      .addCase(updateRoomServiceRequestStatus.fulfilled, (state, { payload }) => {
        state.updating = false;
        const updated = payload?.data;
        if (!updated) return;
        if (updated.status === 'Handled' && state.pendingCount > 0) state.pendingCount -= 1;
        const idx = state.roomServiceRequests.findIndex((r) => r._id === updated.requestId);
        if (idx !== -1) state.roomServiceRequests[idx].status = updated.status;
      })
      .addCase(updateRoomServiceRequestStatus.rejected, (state) => {
        state.updating = false;
      });
  }
});

export default serviceRequestSlice.reducer;
